import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TableDatumInput, TableDatumInputVal } from "../../../../../api/domain/table-data";
import type { TableForm } from "../../../../../api/infrastructure/table-appendix.formatter";
import type { SchemaField, SchemaFieldOption } from "../../../../../helpers/format-databases";
import { getTableColumnOptions } from "../../../../shared/get-table-column-options";
import type { FileInputHandle } from "../../../utils/SchemaFieldTypeInputs/FileInput/FileInput";
import SchemaFieldInput from "../../../utils/SchemaFieldTypeInputs/SchemaFieldInput";
import "./index.scss";

interface SearchTableDataFilterModalProps {
  initialFilterKeyValues?: TableDatumInput;
  tableForm: TableForm[];
  close(): void;
  onChange(filterKeyValues: TableDatumInput): void;
}

export default function SearchTableDataFilterModal({
  tableForm,
  initialFilterKeyValues,
  close,
  onChange,
}: SearchTableDataFilterModalProps) {
  const [inputValues, setInputValues] = useState<TableDatumInput>(new Map());
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const fileInputRef = useRef<FileInputHandle>(null);
  const [errorStatus, setErrorStatus] = useState<Map<string, boolean>>(new Map());
  const wrapAsync = useWrapAsync();

  const handleClearAll = () => {
    setInputValues(new Map());
  };

  const onChangeInputValue = ({ name, value }: { name: string; value?: TableDatumInputVal }) => {
    setInputValues((prevValues) => {
      const newValues = new Map(prevValues);
      newValues.set(name, value);
      return newValues;
    });
  };

  const handleSave = () => {
    onChange(inputValues);
    close();
  };

  const getSchemaFields = useCallback(
    wrapAsync(async () => {
      if (!tableForm) return;
      const loadedSchemaFields: SchemaField[] = [];
      for (const field of tableForm) {
        let options: SchemaFieldOption[] = [];
        if (!field.type) continue;
        if (field.type === "enum") {
          try {
            options = await getTableColumnOptions(field);
          } catch (error) {
            console.error("Error fetching options for field:", field.name, error);
          }
        }
        loadedSchemaFields.push({
          label: field.label,
          name: field.name,
          type: field.type,
          required: false,
          options,
          length: field.length,
          default: field.default ? `${field.default}` : undefined,
        });
      }
      setSchemaFields(loadedSchemaFields);
    }),
    [tableForm],
  );
  useEffect(getSchemaFields, [tableForm]);
  useEffect(() => {
    if (!initialFilterKeyValues) return;
    setInputValues(initialFilterKeyValues);
  }, [initialFilterKeyValues]);

  const renderFields = useMemo(() => {
    return schemaFields.map((field) => {
      const fieldValue = inputValues.get(field.name);
      if (field.type === "file") return;
      return (
        <SchemaFieldInput
          key={field.name}
          field={field}
          value={fieldValue}
          forwardedRef={fileInputRef}
          onChange={onChangeInputValue}
          onError={(val) => {
            const oldErrorStatus = new Map(errorStatus);
            oldErrorStatus.set(field.name, val);
            setErrorStatus(oldErrorStatus);
          }}
        />
      );
    });
  }, [errorStatus, inputValues, schemaFields]);

  return (
    <div className="SidebarModal">
      <div className="SidebarModal-header">
        <h2 className="SidebarModal-headerTitle">Filtres avancés</h2>
        <button
          type="button"
          className="SearchTableDataFilterModal-clearAll"
          onClick={handleClearAll}
        >
          Effacer tous les filtres
        </button>
      </div>

      <div className="SidebarModal-body">
        <p>Filtrer par</p>
        <div className="SearchTableDataFilterModal-formFields">{renderFields}</div>
      </div>

      <div className="SidebarModal-rootActions">
        <Button
          type="button"
          label="Annuler"
          className="SidebarModal-btn Btn secondary outlined"
          onClick={() => {
            close();
          }}
        />
        <Button
          type="button"
          label="Appliquer"
          className="TableColumnEnumForm-btn Btn primary"
          onClick={handleSave}
        />
      </div>
    </div>
  );
}
